import {
  IconButton,
  Paper,
  Tooltip,
  Button as MaterialButton,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ArchiveIcon from '@material-ui/icons/Archive';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import { makeStyles } from '@material-ui/styles';
import { useSnackbar } from 'notistack';
import { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import {
  Link,
  Redirect,
  Switch,
  useLocation,
  useParams,
} from 'react-router-dom';
import { createCheckin } from '../../api/goals';
import { CreateCheckin } from '../../api/types/Checkin';
import { Goal, GoalStatus } from '../../api/types/Goal';
import { PageLoader, RouteACL, SnackMessage } from '../../components/common';
import { GoalModal } from '../../components/founders/dashboard/GoalModal';
import PageTabs from '../../components/goals/page-tabs';
import BaseLayout from '../../components/layout/base-layout';
import { Header } from '../../components/layout/main-layout';
import { GoalProvider, useGoalContext } from '../../contexts/goal-context';
import { UserContext } from '../../contexts/user-context';
import { Pages } from '../../router/constants';
import { ProtectedRouteProps } from '../../router/type';
import { CLASS_TRACKING } from '../../utils/tracking_class';
import GoalCheckinsPage from './goal-checkins-page';
import GoalLogPage from './goal-log-page';
import GoalOverviewPage from './goal-overview-page';
import GoalTasksPage from './goal-tasks-page';

const useStyles = makeStyles({
  container: {
    flexGrow: 1,
    padding: 0,
  },
  content: {
    padding: 40,
  },
  actionsContainer: {
    display: 'flex',
    columnGap: '16px',
  },
});

function GoalPage({ user }: ProtectedRouteProps) {
  const classes = useStyles();
  const { goalId } = useParams<{ goalId: Goal['id'] }>();
  const location = useLocation<{ prevPath?: string }>();
  const [prevPath] = useState(location.state?.prevPath);
  const { goal, isGoalLoading, isVenturesLoading, loadGoal, setGoal } =
    useGoalContext();
  const { hasAccessToAction } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [checkinModalOpen, setCheckinModalOpen] = useState(false);

  useEffect(() => {
    loadGoal(goalId);
  }, [loadGoal, goalId]);

  const handleCreateCheckin = useCallback(
    async (checkinData: CreateCheckin, successMessage?: string) => {
      if (!goal) return;
      try {
        const createdCheckin = await createCheckin(checkinData);
        const updatedGoal: Goal = {
          ...goal,
          progress: createdCheckin.progress,
          status: createdCheckin.status,
        };
        setGoal(updatedGoal);
        enqueueSnackbar(
          successMessage || 'The check-in was successfully added.',
          {
            variant: 'success',
          },
        );
        setCheckinModalOpen(false);
      } catch (e: any) {
        const messageError = e.response?.data?.message;
        enqueueSnackbar(
          'An error occurred while creating the check-in. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
      }
    },
    [goal, setGoal, enqueueSnackbar],
  );

  const handleArchive = useCallback(async () => {
    if (!goal) return;
    const isArchiving = goal.status !== 'ARCHIVED';
    const newStatus: GoalStatus = isArchiving ? 'ARCHIVED' : 'NOT_STARTED';
    const checkinData: CreateCheckin = {
      goalId: goal.id,
      progress: goal.progress,
      status: newStatus,
      contents: '',
    };
    await handleCreateCheckin(
      checkinData,
      isArchiving
        ? 'The goal was successfully archived.'
        : 'The goal was successfully unarchived.',
    );
  }, [goal, handleCreateCheckin]);

  const isArchived = goal?.status === 'ARCHIVED';

  const headerActions = useMemo(() => {
    const actions = [];

    if (hasAccessToAction('goal.checkins.create') && goal) {
      actions.push({
        key: 'check-in',
        label: 'Check-in',
        component: (
          <MaterialButton
            className={CLASS_TRACKING.ENTITY_ACTION}
            data-testid='button-check-in'
            startIcon={<AddIcon />}
            variant='contained'
            color='primary'
            onClick={() => setCheckinModalOpen(true)}>
            Check-in
          </MaterialButton>
        ),
      });
    }

    if (hasAccessToAction('goal.checkins.create') && goal) {
      actions.push({
        key: 'archive',
        label: isArchived ? 'Unarchive' : 'Archive',
        component: (
          <MaterialButton
            className={CLASS_TRACKING.ENTITY_ACTION}
            data-testid={`button-${isArchived ? 'unarchive' : 'archive'}`}
            startIcon={isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
            variant='outlined'
            color='primary'
            onClick={handleArchive}>
            {isArchived ? 'Unarchive' : 'Archive'}
          </MaterialButton>
        ),
      });
    }

    return actions;
  }, [goal, hasAccessToAction, isArchived, handleArchive, setCheckinModalOpen]);

  return (
    <BaseLayout user={user} fullHeight>
      {isGoalLoading || isVenturesLoading ? (
        <PageLoader />
      ) : (
        <>
          <Header
            title={goal?.title || 'Unknown'}
            backLink={
              <Tooltip title='Back to Goals'>
                <IconButton
                  component={Link}
                  to={prevPath || Pages.GOALS}
                  data-testid='button-back-goals'>
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            }
            actions={headerActions}
          />
          <Paper className={classes.container} elevation={1}>
            <PageTabs goalId={goalId} />
            <div className={classes.content}>
              <Switch>
                <RouteACL
                  exact
                  path={Pages.GOAL_OVERVIEW}
                  component={GoalOverviewPage}
                  rule='goal.overview.view'
                  redirectUrl={Pages.GOALS}
                />
                <RouteACL
                  exact
                  path={Pages.GOAL_CHECK_INS}
                  component={GoalCheckinsPage}
                  rule='goal.checkins.view'
                  redirectUrl={Pages.GOAL_CHECK_INS}
                />
                <RouteACL
                  exact
                  path={Pages.GOAL_TASKS}
                  component={GoalTasksPage}
                  rule='goal.tasks.view'
                  redirectUrl={Pages.GOALS}
                />
                <RouteACL
                  exact
                  path={Pages.GOAL_LOG}
                  component={GoalLogPage}
                  rule='goal.log.view'
                  redirectUrl={Pages.GOALS}
                />
                <Redirect to={Pages.GOAL_OVERVIEW} />
              </Switch>
            </div>
          </Paper>
          {checkinModalOpen && goal && (
            <GoalModal
              goal={goal}
              onChange={handleCreateCheckin}
              onClose={() => setCheckinModalOpen(false)}
            />
          )}
        </>
      )}
    </BaseLayout>
  );
}

export default function GoalPageWithProvider(props: ProtectedRouteProps) {
  return (
    <GoalProvider>
      <GoalPage {...props} />
    </GoalProvider>
  );
}
